import React from 'react'
import { Download, FileText, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ExportConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  sessionCount: number
  isExporting?: boolean
}

export const ExportConfirmationDialog: React.FC<ExportConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  sessionCount,
  isExporting = false,
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">
              Export Sessions
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            You are about to export{' '}
            <span className="font-semibold text-gray-900">{sessionCount}</span>{' '}
            session{sessionCount !== 1 ? 's' : ''} to a CSV file.
          </p>

          <div className="bg-primary/10 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-primary/90 mb-2">Export Details:</h4>
            <ul className="text-sm text-primary/80 space-y-1">
              <li>• Format: CSV (Comma Separated Values)</li>
              <li>• Includes: All session data for the current filters</li>
            </ul>
          </div>

          {sessionCount > 500 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <span className="font-medium">Large dataset:</span> This export
                contains many sessions and may take a moment to process.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isExporting}
            className={cn(
              "flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50",
              isExporting && "bg-primary/70 cursor-not-allowed"
            )}
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                <span>Export CSV</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
